import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { videoHighlightsService, VideoHighlight } from '../../../shared/services/videoHighlightsService';
import { socialMediaService, SocialMediaUpload } from '../../../shared/services/socialMediaService';
import { videoProcessingJobsService, VideoProcessingJob } from '../../../shared/services/videoProcessingJobsService';
import { matchDetailsService } from '../../../shared/services/matchDetails';
import Toast from '../../../shared/components/ui/Toast';
import Button from '../../../shared/components/ui/Button';
import SocialMediaUploadsTable from '../components/SocialMediaUploadsTable';
import SocialMediaUploadDrawer from '../components/SocialMediaUploadDrawer';
import UploadToSocialMediaModal from '../components/UploadToSocialMediaModal';

interface ToastState {
  isVisible: boolean;
  type: 'success' | 'error' | 'info';
  message: string;
}

const HighlightDetailPage: React.FC = () => {
  const { highlightId } = useParams<{ highlightId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrganization } = useOrg();
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';

  const [highlight, setHighlight] = useState<VideoHighlight | null>(null);
  const [matchDetails, setMatchDetails] = useState<any>(null);
  const [uploads, setUploads] = useState<SocialMediaUpload[]>([]);
  const [jobs, setJobs] = useState<VideoProcessingJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    type: 'info',
    message: '',
  });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Get active tab from hash (default to overview)
  const getTabFromHash = (hash: string): 'overview' | 'social-media' | 'processing' | 'actions' => {
    const cleanHash = hash.replace('#', '');
    if (cleanHash === 'social-media') return 'social-media';
    if (cleanHash === 'processing') return 'processing';
    if (cleanHash === 'actions') return 'actions';
    return 'overview'; // default
  };

  const [activeTab, setActiveTab] = useState<'overview' | 'social-media' | 'processing' | 'actions'>(() =>
    getTabFromHash(location.hash)
  );

  // Update tab when hash changes
  useEffect(() => {
    const newTab = getTabFromHash(location.hash);
    setActiveTab(newTab);
  }, [location.hash]);

  // Load highlight data
  useEffect(() => {
    if (!highlightId) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load highlight
        const highlightResult = await videoHighlightsService.getHighlightById(highlightId);
        if (!highlightResult.success || !highlightResult.data) {
          setToast({
            isVisible: true,
            type: 'error',
            message: highlightResult.error || 'Highlight not found',
          });
          navigate(`/o/${orgId}/capture/match-assets`);
          return;
        }

        const highlightData = highlightResult.data;
        setHighlight(highlightData);

        // Load match details
        const match = await matchDetailsService.getMatchDetails(highlightData.match_id);
        setMatchDetails(match);

        // Load uploads
        const uploadsResult = await socialMediaService.getUploadsForHighlight(highlightId);
        if (uploadsResult.success && uploadsResult.data) {
          setUploads(uploadsResult.data);
        }

        // Load jobs
        loadJobs();
      } catch (error) {
        console.error('Error loading highlight data:', error);
        setToast({
          isVisible: true,
          type: 'error',
          message: 'Failed to load highlight data',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [highlightId, orgId, navigate]);

  // Poll for jobs updates every 5 seconds
  useEffect(() => {
    if (!highlightId) return;

    const pollJobs = async () => {
      const result = await videoProcessingJobsService.getJobsByHighlightId(highlightId);
      if (result.success && result.data) {
        setJobs(result.data);
      }
    };

    // Load immediately
    pollJobs();

    // Poll every 5 seconds if there are active jobs
    const interval = setInterval(() => {
      pollJobs();
    }, 5000);

    return () => clearInterval(interval);
  }, [highlightId]);

  // Poll for upload status updates every 5 seconds
  useEffect(() => {
    if (!highlightId) return;

    const interval = setInterval(async () => {
      const result = await socialMediaService.getUploadsForHighlight(highlightId);
      if (result.success && result.data) {
        setUploads(result.data);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [highlightId]);

  const loadJobs = async () => {
    if (!highlightId) return;
    setIsLoadingJobs(true);
    try {
      const result = await videoProcessingJobsService.getJobsByHighlightId(highlightId);
      if (result.success && result.data) {
        setJobs(result.data);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const handleTabChange = (tabId: 'overview' | 'social-media' | 'processing' | 'actions') => {
    setActiveTab(tabId);
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  const handleBackClick = () => {
    navigate(`/o/${orgId}/capture/match-assets`);
  };

  const handleEditHighlight = () => {
    // TODO: Navigate to edit highlight page or open modal
    setToast({
      isVisible: true,
      type: 'info',
      message: 'Edit functionality coming soon',
    });
  };

  const handleDeleteHighlight = () => {
    // TODO: Open delete modal
    setToast({
      isVisible: true,
      type: 'info',
      message: 'Delete functionality coming soon',
    });
  };

  const handleUploadRowClick = (uploadId: string) => {
    setSelectedUploadId(uploadId);
    setIsDrawerOpen(true);
  };

  const handleUploadViewClick = (uploadId: string) => {
    setSelectedUploadId(uploadId);
    setIsDrawerOpen(true);
  };

  const handleUploadDeleteClick = async (uploadId: string) => {
    try {
      const result = await socialMediaService.deleteUpload(uploadId);
      if (result.success) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Upload deleted successfully',
        });
        // Reload uploads
        const uploadsResult = await socialMediaService.getUploadsForHighlight(highlightId!);
        if (uploadsResult.success && uploadsResult.data) {
          setUploads(uploadsResult.data);
        }
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to delete upload',
        });
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to delete upload',
      });
    }
  };

  const formatDate = (iso?: string): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  // Get URLs for highlights
  const getHighlightUrls = () => {
    if (!highlight) return { enhancedUrl: null, outputUrl: null };

    const enhancedUrl = highlight.enhanced_url
      ? highlight.enhanced_url
      : highlight.enhanced_storage_path
      ? `https://cdn.capture.kridah.com/${highlight.enhanced_storage_path}`
      : null;

    const outputUrl = highlight.output_url
      ? highlight.output_url
      : highlight.output_storage_path
      ? `https://cdn.capture.kridah.com/${highlight.output_storage_path}`
      : null;

    return { enhancedUrl, outputUrl };
  };

  const { enhancedUrl, outputUrl } = getHighlightUrls();

  const getJobStatusBadge = (status: string) => {
    const styles = {
      queued: 'bg-gray-100 text-gray-800 border border-gray-200',
      running: 'bg-blue-100 text-blue-800 border border-blue-200',
      succeeded: 'bg-green-100 text-green-800 border border-green-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
    };
    return styles[status as keyof typeof styles] || styles.queued;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'social-media', label: 'Social Media Uploads', icon: '📤' },
    { id: 'processing', label: 'Processing Jobs', icon: '⚙️' },
    { id: 'actions', label: 'Actions', icon: '⚡' },
  ] as const;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!highlight) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Highlight not found</p>
          <Button onClick={handleBackClick} variant="ghost" className="mt-4">
            Back to Match Assets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-4 sm:px-6 py-3">
        <div className="w-full flex items-center justify-between">
          <Button
            onClick={handleBackClick}
            variant="ghost"
            size="sm"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            }
            iconPosition="left"
          >
            Back
          </Button>

          {/* Tabs */}
          <div className="flex justify-center space-x-4 sm:space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`py-2 px-2 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-gray-400 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Empty for now */}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 sm:p-6">
        <div className="w-full">
          {/* Tab 1: Overview */}
          {activeTab === 'overview' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Name</span>
                    <span className="text-sm text-gray-900 text-right max-w-xs">{highlight.name}</span>
                  </div>
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Updated At</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(highlight.updated_at)}</span>
                  </div>
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Status</span>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      ['stitched', 'completed'].includes(highlight.status)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {highlight.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Highlight Information */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Highlight Information</h2>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Match ID</span>
                    <span className="text-sm text-gray-900 font-mono text-right">{highlight.match_id}</span>
                  </div>
                  {matchDetails && (
                    <>
                      {matchDetails.home_team && matchDetails.away_team && (
                        <div className="flex items-start justify-between">
                          <span className="text-sm text-gray-500">Match</span>
                          <span className="text-sm text-gray-900 text-right">
                            {matchDetails.home_team.name} vs {matchDetails.away_team.name}
                          </span>
                        </div>
                      )}
                      {matchDetails.tournament && (
                        <div className="flex items-start justify-between">
                          <span className="text-sm text-gray-500">Tournament</span>
                          <span className="text-sm text-gray-900 text-right">{matchDetails.tournament.name}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Status</span>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      ['stitched', 'completed'].includes(highlight.status)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {highlight.status}
                    </span>
                  </div>
                  {highlight.segments_data && Array.isArray(highlight.segments_data) && (
                    <div className="flex items-start justify-between">
                      <span className="text-sm text-gray-500">Segments</span>
                      <span className="text-sm text-gray-900 text-right">{highlight.segments_data.length} segments</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Created At</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(highlight.created_at)}</span>
                  </div>
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Last Updated</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(highlight.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Output URLs */}
              {(enhancedUrl || outputUrl) && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Output</h2>
                  <div className="space-y-3">
                    {enhancedUrl && (
                      <div>
                        <label className="text-xs text-gray-500">Enhanced Video URL</label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-900 font-mono flex-1 truncate">{enhancedUrl}</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(enhancedUrl);
                              setToast({
                                isVisible: true,
                                type: 'success',
                                message: 'URL copied to clipboard',
                              });
                            }}
                            className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                          >
                            Copy
                          </button>
                          <a
                            href={enhancedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                          >
                            View
                          </a>
                        </div>
                      </div>
                    )}
                    {outputUrl && (
                      <div>
                        <label className="text-xs text-gray-500">Stitched Video URL</label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-900 font-mono flex-1 truncate">{outputUrl}</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(outputUrl);
                              setToast({
                                isVisible: true,
                                type: 'success',
                                message: 'URL copied to clipboard',
                              });
                            }}
                            className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                          >
                            Copy
                          </button>
                          <a
                            href={outputUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                          >
                            View
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Social Media Uploads */}
          {activeTab === 'social-media' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Social Media Uploads</h2>
                <Button
                  onClick={() => setIsUploadModalOpen(true)}
                  variant="primary"
                  size="sm"
                >
                  Upload to Social Media
                </Button>
              </div>
              {uploads.length === 0 ? (
                <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg">
                  <p className="font-medium text-gray-700 mb-1">No uploads found</p>
                  <p className="text-sm">Upload this highlight to social media platforms</p>
                </div>
              ) : (
                <SocialMediaUploadsTable
                  uploads={uploads}
                  isLoading={false}
                  onRowClick={handleUploadRowClick}
                  onViewClick={handleUploadViewClick}
                  onDeleteClick={handleUploadDeleteClick}
                  searchQuery=""
                />
              )}
            </div>
          )}

          {/* Tab 3: Processing Jobs */}
          {activeTab === 'processing' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Processing Jobs</h2>
              {isLoadingJobs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No processing jobs</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${getJobStatusBadge(job.status)}`}>
                            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                          </span>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {job.job_type.replace('_', ' ')}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(job.created_at)}</span>
                      </div>
                      {job.error_message && (
                        <p className="text-xs text-red-600 mt-2">{job.error_message}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Actions */}
          {activeTab === 'actions' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
              
              {/* Check for active uploads */}
              {uploads.length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This highlight has active social media uploads. Deleting R2 files will not affect uploads.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Button
                  onClick={handleEditHighlight}
                  variant="primary"
                  className="w-full"
                >
                  Edit Highlight
                </Button>
                <Button
                  onClick={handleDeleteHighlight}
                  variant="ghost"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Delete Videos
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      {highlight && (
        <UploadToSocialMediaModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          entityId={highlight.id}
          entityType="highlight"
          onSuccess={() => {
            // Reload uploads
            const loadUploads = async () => {
              const uploadsResult = await socialMediaService.getUploadsForHighlight(highlight.id);
              if (uploadsResult.success && uploadsResult.data) {
                setUploads(uploadsResult.data);
              }
            };
            loadUploads();
          }}
        />
      )}

      {/* Drawer */}
      <SocialMediaUploadDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUploadId(null);
        }}
        uploadId={selectedUploadId}
        onDelete={async (uploadId) => {
          await handleUploadDeleteClick(uploadId);
        }}
        onRetry={async () => {
          if (highlightId) {
            const uploadsResult = await socialMediaService.getUploadsForHighlight(highlightId);
            if (uploadsResult.success && uploadsResult.data) {
              setUploads(uploadsResult.data);
            }
          }
        }}
      />

      {/* Toast */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast({ ...toast, isVisible: false })}
        />
      )}
    </div>
  );
};

export default HighlightDetailPage;

